import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Settings2, Copy, Trash2, Mic, Image as ImageIcon, Check, ExternalLink, Volume2, VolumeX, Square, MessageSquarePlus, History } from "lucide-react";
import Modal from "@/components/chat/Modal";
import TypewriterText from "@/components/chat/TypewriterText";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const QUICK = ["Summarize this text", "Draft a polite reply", "Explain simply", "Translate to French"];
const PROVIDERS = ["groq", "gemini", "openrouter"];
const GUIDES = {
  groq: {
    label: "Groq", url: "https://console.groq.com/keys", prefix: "gsk_",
    steps: ["Tap « Open Groq Console »", "Sign in (Google or email)", "Tap « Create API Key »", "Copy the key (starts with gsk_)", "Come back here and paste it"],
  },
  gemini: {
    label: "Google AI Studio", url: "https://aistudio.google.com/app/apikey", prefix: "AIza",
    steps: ["Tap « Open Google AI Studio »", "Sign in with your Google account", "Tap « Create API key »", "Copy the key (starts with AIza)", "Come back here and paste it"],
  },
  openrouter: {
    label: "OpenRouter", url: "https://openrouter.ai/keys", prefix: "sk-or-",
    steps: ["Tap « Open OpenRouter »", "Sign in (Google/GitHub/email)", "Tap « Create Key »", "Copy the key (starts with sk-or-)", "Come back here and paste it"],
  },
};

export default function AIDialog({ onClose }) {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const inputRef = useRef(null);
  const [provider, setProvider] = useState("");
  const [loading, setLoading] = useState(false);
  const [showKeys, setShowKeys] = useState(false);
  const [keys, setKeys] = useState({ groq: "", gemini: "", openrouter: "" });
  const [configured, setConfigured] = useState({});
  const [image, setImage] = useState(null);
  const [keyProvider, setKeyProvider] = useState("gemini");
  const [autoSpeak, setAutoSpeak] = useState(() => localStorage.getItem("rc_ai_autospeak") === "1");
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState(() => localStorage.getItem("rc_ai_voice") || "");
  const [rate, setRate] = useState(() => parseFloat(localStorage.getItem("rc_ai_rate") || "1") || 1);
  const [convId, setConvId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selMode, setSelMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const endRef = useRef(null);

  useEffect(() => { api.get("/ai/keys").then(({ data }) => setConfigured(data)).catch(() => {}); }, []);
  useEffect(() => {
    (async () => {
      const convs = await loadConversations();
      if (convs.length) await openConversation(convs[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const load = () => setVoices(synth.getVoices() || []);
    load();
    synth.addEventListener?.("voiceschanged", load);
    return () => { synth.removeEventListener?.("voiceschanged", load); try { synth.cancel(); } catch {} };
  }, []);

  const stopSpeak = () => { try { window.speechSynthesis?.cancel(); } catch {} setSpeakingIdx(null); };

  const buildUtterance = (text) => {
    const clean = String(text || "").replace(/[*_#`>]/g, "").trim();
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = lang === "fr" ? "fr-FR" : "en-US";
    const vlist = window.speechSynthesis.getVoices();
    let v = voiceURI && vlist.find((vo) => vo.voiceURI === voiceURI);
    if (!v) v = vlist.find((vo) => (vo.lang || "").toLowerCase().startsWith(u.lang.slice(0, 2)));
    if (v) { u.voice = v; u.lang = v.lang; }
    u.rate = rate;
    return u;
  };

  const speak = (text, idx) => {
    const synth = window.speechSynthesis;
    if (!synth) { toast.error("Voice not supported in this browser"); return; }
    synth.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = buildUtterance(text);
    if (!u.text) return;
    u.onend = () => setSpeakingIdx((c) => (c === idx ? null : c));
    u.onerror = () => setSpeakingIdx(null);
    setSpeakingIdx(idx);
    synth.speak(u);
  };

  const previewVoice = () => {
    const synth = window.speechSynthesis;
    if (!synth) { toast.error("Voice not supported in this browser"); return; }
    synth.cancel(); setSpeakingIdx(null);
    synth.speak(buildUtterance(lang === "fr" ? "Bonjour, ceci est un aperçu de la voix." : "Hi, this is a voice preview."));
  };

  const changeVoice = (uri) => { setVoiceURI(uri); localStorage.setItem("rc_ai_voice", uri); };
  const changeRate = (r) => { setRate(r); localStorage.setItem("rc_ai_rate", String(r)); };

  const toggleAutoSpeak = () => {
    setAutoSpeak((s) => {
      const next = !s;
      localStorage.setItem("rc_ai_autospeak", next ? "1" : "0");
      if (!next) stopSpeak();
      toast(next ? (lang === "fr" ? "Lecture auto activée" : "Auto read-aloud on") : (lang === "fr" ? "Lecture auto désactivée" : "Auto read-aloud off"));
      return next;
    });
  };

  const saveKeys = async () => {
    const payload = Object.fromEntries(Object.entries(keys).filter(([, v]) => v));
    await api.put("/ai/keys", payload);
    const { data } = await api.get("/ai/keys");
    setConfigured(data);
    setShowKeys(false);
    toast.success("Keys saved securely");
  };

  const removeKey = async (p) => {
    await api.put("/ai/keys", { [p]: "" });
    const { data } = await api.get("/ai/keys");
    setConfigured(data);
    setKeys((k) => ({ ...k, [p]: "" }));
    if (provider === p) setProvider("");
    toast.success(`${GUIDES[p].label} · ${t("key_removed")}`);
  };

  const loadConversations = async () => {
    try { const { data } = await api.get("/ai/conversations"); setConversations(data || []); return data || []; }
    catch { return []; }
  };

  const openConversation = async (id) => {
    try {
      const { data } = await api.get(`/ai/conversations/${id}/messages`, { params: { limit: 20 } });
      setMessages((data.messages || []).map((m) => ({ role: m.role, content: m.content, meta: m.meta })));
      setConvId(id);
      setShowHistory(false);
      setShowKeys(false);
    } catch { toast.error("Could not load conversation"); }
  };

  const newChat = () => { stopSpeak(); setMessages([]); setConvId(null); setShowHistory(false); setInput(""); };

  const finishTyping = (idx, content) => {
    setMessages((ms) => ms.map((m, i) => (i === idx ? { ...m, typing: false } : m)));
    if (autoSpeak) speak(content, idx);
  };

  const deleteConversation = async (id) => {
    await api.delete(`/ai/conversations/${id}`);
    await loadConversations();
    if (id === convId) { setMessages([]); setConvId(null); }
    setSelected((s) => s.filter((x) => x !== id));
    toast.success(lang === "fr" ? "Conversation supprimée" : "Conversation deleted");
  };

  const deleteSelected = async () => {
    if (!selected.length) return;
    await api.post("/ai/conversations/delete-many", { ids: selected });
    const wasCurrent = selected.includes(convId);
    await loadConversations();
    if (wasCurrent) { setMessages([]); setConvId(null); }
    setSelected([]); setSelMode(false);
    toast.success(lang === "fr" ? "Supprimé" : "Deleted");
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content && !image) return;
    const next = [...messages, { role: "user", content: content || "(image)" }];
    setMessages(next);
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto"; // reset grown height after send
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { messages: next.map(({ role, content }) => ({ role, content })), provider: provider || undefined, image, conversation_id: convId || undefined });
      if (data.error) {
        toast.error(data.error);
        if (/no ai keys|api key|invalid key|rejected|unauthor/i.test(data.error)) setShowKeys(true);
        setMessages([...next, { role: "assistant", content: data.error, error: true }]);
      } else {
        setMessages([...next, { role: "assistant", content: data.content, meta: `${data.provider} · ${data.model}`, typing: true }]);
        if (data.conversation_id) setConvId(data.conversation_id);
        loadConversations();
      }
      setImage(null);
    } catch (e) {
      const detail = e.response?.data?.detail || "Request failed — please try again";
      toast.error(detail);
      setMessages([...next, { role: "assistant", content: detail, error: true }]);
    } finally { setLoading(false); }
  };

  const dictate = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Voice not supported in this browser");
    const rec = new SR();
    rec.lang = "en-US";
    rec.onresult = (e) => setInput((i) => i + e.results[0][0].transcript);
    rec.start();
    toast("Listening…");
  };

  const onImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => { setImage(r.result); setProvider("gemini"); toast("Image attached (uses Gemini)"); };
    r.readAsDataURL(f);
  };

  return (
    <Modal title="AI Assistant" onClose={onClose} bare>
      <div className="flex-1 min-h-0 flex flex-col max-w-2xl mx-auto w-full px-4 pt-3 pb-3 rc-safe-bottom">
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[var(--rc-muted)]">Model:</span>
          <select value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="ai-provider-select"
            className="bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-full px-3 py-1.5 focus:outline-none">
            <option value="">Auto (fallback)</option>
            {PROVIDERS.map((p) => <option key={p} value={p}>{p}{configured[p] ? " ✓" : ""}</option>)}
          </select>
        </div>
        <div className="flex gap-1">
          <button onClick={toggleAutoSpeak} title={lang === "fr" ? "Lecture à voix haute" : "Read answers aloud"} data-testid="ai-autospeak-btn"
            className={`h-8 w-8 grid place-items-center rounded-full hover:bg-[var(--rc-bg)] ${autoSpeak ? "text-[var(--rc-gold)]" : "text-[var(--rc-muted)]"}`}>
            {autoSpeak ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <button onClick={() => { const n = !showHistory; setShowHistory(n); setShowKeys(false); setSelMode(false); setSelected([]); if (n) loadConversations(); }} title={lang === "fr" ? "Historique" : "History"} data-testid="ai-history-btn"
            className={`h-8 w-8 grid place-items-center rounded-full hover:bg-[var(--rc-bg)] ${showHistory ? "text-[var(--rc-gold)]" : "text-[var(--rc-muted)]"}`}><History size={16} /></button>
          <button onClick={newChat} title={lang === "fr" ? "Nouveau chat" : "New chat"} data-testid="ai-newchat-btn" className="h-8 w-8 grid place-items-center rounded-full hover:bg-[var(--rc-bg)] text-[var(--rc-muted)]"><MessageSquarePlus size={16} /></button>
          <button onClick={() => { setShowKeys((s) => !s); setShowHistory(false); }} title="Keys" data-testid="ai-keys-btn" className="h-8 w-8 grid place-items-center rounded-full hover:bg-[var(--rc-bg)] text-[var(--rc-gold)]"><Settings2 size={16} /></button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
      {showHistory && (
        <div className="space-y-2" data-testid="ai-history-panel">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold">{lang === "fr" ? "Conversations" : "Conversations"}</span>
            <div className="flex items-center gap-3">
              {conversations.length > 0 && (
                <button onClick={() => { setSelMode((m) => !m); setSelected([]); }} data-testid="ai-history-select" className="text-xs text-[var(--rc-gold)] font-medium">
                  {selMode ? (lang === "fr" ? "Annuler" : "Cancel") : (lang === "fr" ? "Sélectionner" : "Select")}
                </button>
              )}
              {selMode && selected.length > 0 && (
                <button onClick={deleteSelected} data-testid="ai-history-delete-selected" className="text-xs text-red-400 font-medium">
                  {(lang === "fr" ? "Supprimer" : "Delete")} ({selected.length})
                </button>
              )}
            </div>
          </div>
          {conversations.length === 0 && <p className="text-center text-sm text-[var(--rc-muted)] py-12">{lang === "fr" ? "Aucune conversation" : "No conversations yet"}</p>}
          {conversations.map((c) => (
            <div key={c.id} data-testid={`ai-conv-${c.id}`}
              onClick={() => (selMode ? toggleSelect(c.id) : openConversation(c.id))}
              className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition-colors ${c.id === convId ? "border-[var(--rc-gold)]/40 bg-[var(--rc-gold)]/5" : "border-[var(--rc-border)] hover:bg-[var(--rc-bg)]"}`}>
              {selMode && (
                <span className={`h-5 w-5 shrink-0 rounded-full border grid place-items-center ${selected.includes(c.id) ? "bg-[var(--rc-gold)] border-[var(--rc-gold)] text-black" : "border-[var(--rc-border)]"}`}>
                  {selected.includes(c.id) && <Check size={12} />}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{c.title || "New chat"}</div>
                <div className="text-xs text-[var(--rc-muted)] truncate">{c.preview || "—"}</div>
              </div>
              {!selMode && (
                <button onClick={(e) => { e.stopPropagation(); deleteConversation(c.id); }} data-testid={`ai-conv-delete-${c.id}`}
                  className="h-8 w-8 grid place-items-center rounded-full text-red-400 hover:bg-[var(--rc-bg)] shrink-0"><Trash2 size={15} /></button>
              )}
            </div>
          ))}
        </div>
      )}
      {!showHistory && (<>
      {showKeys && (
        <div className="mb-3 p-4 rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] space-y-3 rc-pop">
          <p className="text-xs text-[var(--rc-muted)]">Choose a free provider and paste your API key. It stays on your account only.</p>
          <div className="grid grid-cols-3 gap-2">
            {PROVIDERS.map((p) => (
              <button key={p} onClick={() => setKeyProvider(p)} data-testid={`ai-provider-tab-${p}`}
                className={`rounded-xl py-2 text-sm font-medium border transition-colors ${keyProvider === p ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}>
                {GUIDES[p].label}{configured[p] ? " ✓" : ""}
              </button>
            ))}
          </div>
          <input type="password" placeholder={`${GUIDES[keyProvider].label} API key`} value={keys[keyProvider]}
            onChange={(e) => setKeys({ ...keys, [keyProvider]: e.target.value })} data-testid={`ai-key-${keyProvider}`}
            className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
          {configured[keyProvider] && (
            <div className="flex items-center justify-between rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-2 text-sm">
              <span className="flex items-center gap-1.5 text-[var(--rc-gold)]"><Check size={14} /> {GUIDES[keyProvider].label} — configured</span>
              <button onClick={() => removeKey(keyProvider)} data-testid={`ai-remove-key-${keyProvider}`}
                className="flex items-center gap-1.5 text-red-400 hover:text-red-300 font-medium">
                <Trash2 size={14} /> {t("remove_key")}
              </button>
            </div>
          )}
          <a href={GUIDES[keyProvider].url} target="_blank" rel="noreferrer" data-testid="ai-getkey-link"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--rc-gold)] font-medium">
            <ExternalLink size={14} /> {t("get_free_key")}
          </a>
          <div className="rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-3">
            <p className="text-sm font-semibold mb-2">{t("how_get_key")}</p>
            <ol className="space-y-2">
              {GUIDES[keyProvider].steps.map((s, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm">
                  <span className="shrink-0 h-6 w-6 grid place-items-center rounded-full border border-[var(--rc-gold)]/40 text-[var(--rc-gold)] text-xs">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
            <a href={GUIDES[keyProvider].url} target="_blank" rel="noreferrer" data-testid="ai-openconsole-btn"
              className="mt-3 w-full flex items-center justify-center gap-2 rounded-full bg-white text-black font-semibold py-2.5 text-sm">
              <ExternalLink size={15} /> Open {GUIDES[keyProvider].label}
            </a>
          </div>
          <div className="rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-3 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold"><Volume2 size={15} className="text-[var(--rc-gold)]" /> {lang === "fr" ? "Voix de l'assistant" : "Assistant voice"}</div>
            <div className="flex items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-sm">
                <span>{lang === "fr" ? "Lecture auto" : "Auto read-aloud"}</span>
              </label>
              <button type="button" onClick={toggleAutoSpeak} data-testid="ai-autospeak-toggle"
                className={`relative h-6 w-11 rounded-full transition-colors ${autoSpeak ? "bg-[var(--rc-gold)]" : "bg-[var(--rc-border)]"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${autoSpeak ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
            <div className="space-y-1.5">
              <span className="text-xs text-[var(--rc-muted)]">{lang === "fr" ? "Voix" : "Voice"}</span>
              <select value={voiceURI} onChange={(e) => changeVoice(e.target.value)} data-testid="ai-voice-select"
                className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50">
                <option value="">{lang === "fr" ? "Automatique (selon la langue)" : "Automatic (match language)"}</option>
                {voices.map((v) => <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-[var(--rc-muted)]">
                <span>{lang === "fr" ? "Vitesse" : "Speed"}</span>
                <span className="text-[var(--rc-gold)] font-medium">{rate.toFixed(1)}x</span>
              </div>
              <input type="range" min="0.5" max="2" step="0.1" value={rate} onChange={(e) => changeRate(parseFloat(e.target.value))}
                data-testid="ai-voice-speed" className="w-full accent-[var(--rc-gold)]" />
            </div>
            <button type="button" onClick={previewVoice} data-testid="ai-voice-preview"
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-[var(--rc-gold)]/40 text-[var(--rc-gold)] font-medium py-2 text-sm">
              <Volume2 size={15} /> {lang === "fr" ? "Tester la voix" : "Test voice"}
            </button>
          </div>
          <button onClick={saveKeys} className="w-full rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-2.5 text-sm" data-testid="ai-save-keys-btn">{t("save")}</button>
        </div>
      )}

      <div className={`${messages.length === 0 && !showKeys ? "h-full" : "space-y-4"}`}>
        {messages.length === 0 && !showKeys && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-4 px-4">
            <div className="h-16 w-16 grid place-items-center rounded-2xl bg-gradient-to-br from-[var(--rc-gold)] to-[var(--rc-gold-light)] shadow-lg shadow-[var(--rc-gold)]/20">
              <Sparkles size={30} className="text-black" />
            </div>
            <p className="text-lg font-heading text-[var(--rc-text)]">{lang === "fr" ? "Comment puis-je vous aider ?" : "How can I help you today?"}</p>
            <p className="text-sm text-[var(--rc-muted)] -mt-2">{lang === "fr" ? "Alimenté par vos propres clés." : "Powered by your own keys."}</p>
            <div className="flex flex-wrap justify-center gap-2 mt-1">
              {QUICK.map((qp) => (
                <button key={qp} onClick={() => send(qp)} data-testid={`quick-${qp.slice(0,6)}`}
                  className="text-sm rounded-full border border-[var(--rc-gold)]/30 bg-[var(--rc-gold)]/5 text-[var(--rc-gold)] px-4 py-2 hover:bg-[var(--rc-gold)]/15 transition-colors">{qp}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br-md px-4 py-3 text-[15px] leading-relaxed bg-gradient-to-br from-[var(--rc-gold)] to-[var(--rc-gold-light)] text-black font-medium shadow-md shadow-[var(--rc-gold)]/10 whitespace-pre-wrap break-words">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={i} className="flex justify-start gap-2.5">
              <span className="mt-0.5 h-8 w-8 shrink-0 grid place-items-center rounded-full bg-gradient-to-br from-[var(--rc-gold)] to-[var(--rc-gold-light)]">
                <Sparkles size={16} className="text-black" />
              </span>
              <div className={`max-w-[82%] rounded-2xl rounded-bl-md px-4 py-3 text-[15px] leading-relaxed ${m.error ? "bg-red-500/10 border border-red-500/30 text-red-300" : "bg-[var(--rc-surface-2)] border border-[var(--rc-border)] text-[var(--rc-text)]"}`}>
                {m.typing
                  ? <TypewriterText text={m.content} onDone={() => finishTyping(i, m.content)} onTick={() => endRef.current?.scrollIntoView({ behavior: "auto" })} />
                  : <p className="whitespace-pre-wrap break-words">{m.content}</p>}
                <div className="flex items-center gap-2.5 mt-2 pt-1.5 border-t border-[var(--rc-border)]/40 text-[10px] text-[var(--rc-muted)]">
                  <span>{m.meta}</span>
                  <button onClick={() => { navigator.clipboard.writeText(m.content); toast.success("Copied"); }} className="hover:text-[var(--rc-gold)]" data-testid="ai-copy-btn"><Copy size={13} /></button>
                  {!m.error && (
                    <button onClick={() => speak(m.content, i)} title={lang === "fr" ? "Écouter" : "Listen"} data-testid={`ai-speak-btn-${i}`}
                      className={speakingIdx === i ? "text-[var(--rc-gold)]" : "hover:text-[var(--rc-gold)]"}>
                      {speakingIdx === i ? <Square size={13} /> : <Volume2 size={13} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        ))}
        {loading && (
          <div className="flex justify-start gap-2.5">
            <span className="mt-0.5 h-8 w-8 shrink-0 grid place-items-center rounded-full bg-gradient-to-br from-[var(--rc-gold)] to-[var(--rc-gold-light)]"><Sparkles size={16} className="text-black" /></span>
            <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md bg-[var(--rc-surface-2)] border border-[var(--rc-border)] px-4 py-3">
              <span className="h-2 w-2 rounded-full bg-[var(--rc-gold)] rc-dot" />
              <span className="h-2 w-2 rounded-full bg-[var(--rc-gold)] rc-dot" style={{ animationDelay: "0.2s" }} />
              <span className="h-2 w-2 rounded-full bg-[var(--rc-gold)] rc-dot" style={{ animationDelay: "0.4s" }} />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      </>)}
      </div>

      {image && <div className="mt-2 mb-2 flex items-center gap-2 text-xs text-[var(--rc-gold)] shrink-0"><Check size={14} /> Image attached <button onClick={() => setImage(null)} className="underline">remove</button></div>}

      <div className="flex items-end gap-2 pt-3 shrink-0">
        <label className="h-10 w-10 grid place-items-center rounded-full text-[var(--rc-silver)] hover:text-[var(--rc-gold)] cursor-pointer" data-testid="ai-image-btn">
          <ImageIcon size={18} /><input type="file" accept="image/*" hidden onChange={onImage} />
        </label>
        <button onClick={dictate} className="h-10 w-10 grid place-items-center rounded-full text-[var(--rc-silver)] hover:text-[var(--rc-gold)]" data-testid="ai-dictate-btn"><Mic size={18} /></button>
        <textarea ref={inputRef} rows={1} value={input} onChange={(e) => setInput(e.target.value)}
          onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px"; }}
          placeholder="Ask the assistant…" data-testid="ai-input"
          className="flex-1 min-w-0 resize-none bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-2xl px-4 py-2.5 max-h-36 focus:outline-none focus:border-[var(--rc-gold)]/50" />
        <button onClick={() => send()} disabled={loading} className="h-11 w-11 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black" data-testid="ai-send-btn"><Send size={18} /></button>
      </div>
      </div>
    </Modal>
  );
}
